import { useState, useEffect, useCallback } from 'react';
import { fetchCallImpactData, DEFAULT_STAGE } from '../services/api';
import type { CallImpactResponse } from '../types';

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface UseCallDataReturn {
  data: CallImpactResponse | null;
  loading: boolean;
  error: string | null;
  startDate: string;
  endDate: string;
  stage: string;
  setStartDate: React.Dispatch<React.SetStateAction<string>>;
  setEndDate: React.Dispatch<React.SetStateAction<string>>;
  setStage: React.Dispatch<React.SetStateAction<string>>;
  refetch: () => Promise<void>;
}

export function useCallData(enabled: boolean = true): UseCallDataReturn {
  const today = new Date();
  const [startDate, setStartDate] = useState<string>(formatDate(today));
  const [endDate, setEndDate] = useState<string>(formatDate(today));
  const [stage, setStage] = useState<string>(DEFAULT_STAGE);
  const [data, setData] = useState<CallImpactResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCallImpactData(startDate, endDate, stage);
      setData(result);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, stage]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [enabled, fetchData]);

  return {
    data,
    loading,
    error,
    startDate,
    endDate,
    stage,
    setStartDate,
    setEndDate,
    setStage,
    refetch: fetchData,
  };
}
